import { BodyScrollEndEvent, GridApi, IRowNode } from 'ag-grid-community';
import { startTransition, useCallback, useRef } from 'react';

interface VisibleData {
  symbols: string[];
  fields: string[];
  ids: string[];
  removedSymbols?: string[];
}

interface useViewportData {
  getVisibleData: (gridApi: GridApi | null) => VisibleData;
  onTransactionApplied: (gridApi: GridApi | null, callback: () => void) => void;
  filterVisibleSymbols: (gridApi: GridApi | null, symbols: string[]) => string[];
  initOnScrollEnd: (
    gridApi: GridApi | null,
    callback?: (visibleData: VisibleData, event: BodyScrollEndEvent) => void,
  ) => ((event: BodyScrollEndEvent) => void) | null;
  getRemovedSymbols: (gridApi: GridApi | null) => string[];
}

interface TickerRow {
  symbol?: string;
  ticker?: string;
  data?: { symbol?: string; ticker?: string };
  id?: string;
}

const getTickerSlug = (row: TickerRow | null | undefined) => {
  if (!row) return null;
  return row.symbol ?? row.ticker ?? row.data?.symbol ?? row.data?.ticker ?? row.id ?? null;
};

export const useViewportData = (): useViewportData => {
  const scrollListener = useRef<((event: BodyScrollEndEvent) => void) | null>(null);
  const gridApiRef = useRef<GridApi | null>(null);
  const callbackRef = useRef<((visibleData: VisibleData, event: BodyScrollEndEvent) => void) | null>(null);
  const previouslyRenderedNodes = useRef<IRowNode[]>([]);
  const transactionCallbacksRef = useRef<Set<() => void>>(new Set());
  const transactionListenerRef = useRef<(() => void) | null>(null);
  const transactionGridApiRef = useRef<GridApi | null>(null);

  const getVisibleData = useCallback((gridApi: GridApi | null): VisibleData => {
    const renderedNodes = gridApi?.getRenderedNodes() ?? [];
    const visibleColumns = gridApi?.getAllDisplayedVirtualColumns();
    const symbols = renderedNodes.map(node => getTickerSlug(node.data)).filter((s): s is string => !!s);
    const ids = renderedNodes.flatMap(node => (node.id ? [node.id] : []));
    const fields = (visibleColumns?.map(col => col.getColDef().colId)?.filter(Boolean) as string[]) ?? [];

    return {
      fields,
      ids,
      symbols,
    };
  }, []);

  const getRemovedSymbols = useCallback((gridApi: GridApi | null): string[] => {
    const currentlyRenderedNodes = gridApi?.getRenderedNodes();
    const currentTickers = currentlyRenderedNodes?.map(node => getTickerSlug(node)) ?? [];

    previouslyRenderedNodes.current = previouslyRenderedNodes?.current?.filter(
      node => !currentTickers.includes(getTickerSlug(node)),
    );

    if (previouslyRenderedNodes?.current?.length) {
      const previousTickers = previouslyRenderedNodes.current.map(node => getTickerSlug(node));
      previouslyRenderedNodes.current = currentlyRenderedNodes ?? [];

      return previousTickers?.filter((t): t is string => !!t) ?? [];
    }

    previouslyRenderedNodes.current = currentlyRenderedNodes ?? [];
    return [];
  }, []);

  const initOnScrollEnd = useCallback(
    (gridApi: GridApi | null, callback?: (visibleData: VisibleData, event: BodyScrollEndEvent) => void) => {
      callbackRef.current = callback ?? null;

      if (scrollListener.current && gridApiRef.current) {
        gridApiRef.current.removeEventListener('bodyScrollEnd', scrollListener.current);
        scrollListener.current = null;
      }

      previouslyRenderedNodes.current = gridApi?.getRenderedNodes() ?? [];
      gridApiRef.current = gridApi;

      if (!gridApi) {
        return null;
      }

      scrollListener.current =
        gridApi.addEventListener('bodyScrollEnd', (event: BodyScrollEndEvent) => {
          const api = gridApiRef.current;
          if (!api || !callbackRef.current) {
            return;
          }
          callbackRef.current(
            {
              ...getVisibleData(api),
              removedSymbols: event?.direction === 'vertical' ? getRemovedSymbols(api) : [],
            },
            event,
          );
        }) ?? null;

      return scrollListener.current;
    },
    [getVisibleData, getRemovedSymbols],
  );

  const onTransactionApplied = useCallback((gridApi: GridApi | null, callback: () => void) => {
    if (!gridApi || !callback) return;

    if (transactionGridApiRef.current && transactionGridApiRef.current !== gridApi) {
      if (transactionListenerRef.current) {
        transactionGridApiRef.current.removeEventListener('asyncTransactionsFlushed', transactionListenerRef.current);
      }
      transactionCallbacksRef.current.clear();
      transactionListenerRef.current = null;
    }

    transactionGridApiRef.current = gridApi;
    transactionCallbacksRef.current.add(callback);

    if (transactionListenerRef.current) {
      return;
    }

    const onTransactionAppliedCallback = () => {
      previouslyRenderedNodes.current = gridApi?.getRenderedNodes() ?? [];
      const callbacks = [...transactionCallbacksRef.current];
      transactionCallbacksRef.current.clear();
      transactionListenerRef.current = null;
      gridApi.removeEventListener('asyncTransactionsFlushed', onTransactionAppliedCallback);
      callbacks.forEach(cb => startTransition(cb));
    };

    transactionListenerRef.current = onTransactionAppliedCallback;
    gridApi.addEventListener('asyncTransactionsFlushed', onTransactionAppliedCallback);
  }, []);

  const filterVisibleSymbols = useCallback(
    (gridApi: GridApi | null, symbols: string[]): string[] => {
      const { symbols: visibleSymbols } = getVisibleData(gridApi);
      return symbols?.filter(symbol => visibleSymbols.includes(symbol));
    },
    [getVisibleData],
  );

  return {
    filterVisibleSymbols,
    getRemovedSymbols,
    getVisibleData,
    initOnScrollEnd,
    onTransactionApplied,
  };
};
